import type React from 'react';
import type { ElementType } from 'react';
import { twMerge } from 'tailwind-merge';
import type * as TF from 'type-fest';

import type { Color } from '#/ui/types';
import type { OverridableStringUnion, OverrideProps } from '#/ui/utils/types';
import Box from './box';

export type IconButtonPropsColorOverrides = TF.EmptyObject;

export type IconButtonPropsSizeOverrides = TF.EmptyObject;

export type IconButtonPropsShapeOverrides = TF.EmptyObject;

export type IconButtonPropsVariantOverrides = TF.EmptyObject;

export interface IconButtonOwnProps {
  /**
   * The content of the component.
   */
  children?: React.ReactNode;
  /**
   * The color of the component.
   * @default 'default'
   */
  color?: OverridableStringUnion<Color, IconButtonPropsColorOverrides>;
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * The size of the component.
   * @default 'medium'
   */
  size?: OverridableStringUnion<'small' | 'medium' | 'large', IconButtonPropsSizeOverrides>;
  /**
   * The shape of the button.
   * @default 'rounded'
   */
  shape?: OverridableStringUnion<'circle' | 'rounded' | 'square', IconButtonPropsShapeOverrides>;
  /**
   * The variant to use.
   * @default 'text'
   */
  variant?: OverridableStringUnion<
    'text' | 'contained' | 'outlined',
    IconButtonPropsVariantOverrides
  >;
  /**
   * The aria-label of the button element.
   */
  'aria-label'?: string;
}

export type IconButtonTypeMap<
  AdditionalProps = TF.EmptyObject,
  RootComponent extends React.ElementType = 'button',
> = {
  props: AdditionalProps & IconButtonOwnProps;
  defaultComponent: RootComponent;
};

export type IconButtonProps<
  RootComponent extends React.ElementType = IconButtonTypeMap['defaultComponent'],
  AdditionalProps = TF.EmptyObject,
  // @ts-expect-error
> = OverrideProps<IconButtonTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

function IconButton<C extends ElementType = 'button'>(
  {
    component = 'button',
    children,
    color = 'primary',
    disabled = false,
    size = 'medium',
    shape = 'rounded',
    variant = 'text',
    className,
    'aria-label': ariaLabel,
    ...props
  }: IconButtonProps<C>,
  ref?: React.ForwardedRef<C>
) {
  const variantClassnames = getVariantClassnames(variant, color);
  const sizeClassnames = getSizeClassnames(size);
  const shapeClassnames = getShapeClassnames(shape);

  const buttonClasses = twMerge(
    'gooey-icon-d-btn',
    'd-btn',
    shapeClassnames,
    variantClassnames,
    sizeClassnames,
    disabled && 'd-btn-disabled',
    className
  );

  return (
    <Box
      {...props}
      component={component}
      ref={ref}
      className={buttonClasses}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {children}
    </Box>
  );
}

IconButton.displayName = 'IconButton';

export default IconButton;

/*
|------------------
| Internals
|------------------
*/

const variantMap = {
  contained: {
    primary: 'd-btn-primary',
    secondary: 'd-btn-secondary',
    accent: 'd-btn-accent',
    error: 'd-btn-error',
    warning: 'd-btn-warning',
    info: 'd-btn-info',
    success: 'd-btn-success',
  },
  outlined: {
    primary: 'd-btn-outline d-btn-primary',
    secondary: 'd-btn-outline d-btn-secondary',
    accent: 'd-btn-outline d-btn-accent',
    error: 'd-btn-outline d-btn-error',
    warning: 'd-btn-outline d-btn-warning',
    info: 'd-btn-outline d-btn-info',
    success: 'd-btn-outline d-btn-success',
  },
  text: {
    primary: 'd-btn-ghost text-primary-contrast',
    secondary: 'd-btn-ghost text-secondary-contrast',
    accent: 'd-btn-ghost text-accent-contrast',
    error: 'd-btn-ghost text-error-contrast',
    warning: 'd-btn-ghost text-warning-contrast',
    info: 'd-btn-ghost text-info-contrast',
    success: 'd-btn-ghost text-success-contrast',
  },
} as const;

const sizeMap = {
  small: 'd-btn-sm w-8 h-8 min-h-0',
  medium: 'w-10 h-10 min-h-0',
  large: 'd-btn-lg w-12 h-12 min-h-0',
} as const;

const shapeMap = {
  circle: 'd-btn-circle',
  rounded: 'd-btn-square rounded',
  square: 'd-btn-square rounded-none',
} as const;

function getSizeClassnames(size: IconButtonProps['size']) {
  return sizeMap[size ?? 'medium'];
}

function getVariantClassnames(
  variant: IconButtonProps['variant'] = 'text',
  color: IconButtonProps['color'] = 'primary'
) {
  return variantMap[variant][color];
}

function getShapeClassnames(shape: IconButtonProps['shape']) {
  return shapeMap[shape ?? 'circle'];
}
